import React from 'react';

const coreValues = [
  { icon: 'ri-lightbulb-flash-line', title: 'Relentless Innovation', text: 'We don’t follow trends; we set them. Our team is constantly exploring AI to keep your agency ahead of the curve.' },
  { icon: 'ri-shield-user-line', title: 'Customer First', text: 'Zotrai is built on the feedback of travel agents worldwide. Your business growth is our primary success metric.' },
  { icon: 'ri-rocket-line', title: 'Scalable Infrastructure', text: 'Our platform is designed to scale with you, whether you are a boutique agency or a global enterprise.' },
  { icon: 'ri-shake-hands-line', title: 'Integrity & Trust', text: 'Transparent pricing, secure data handling, and reliable 24/7 support are the core pillars of our operations.' },
];

const whyChooseUs = [
  { icon: 'ri-magic-line', title: 'AI-Powered Planning', text: 'Transform manual 40-minute itinerary tasks into 60-second AI-generated professional plans.' },
  { icon: 'ri-global-line', title: 'Global Inventory', text: 'Direct API access to 50+ global suppliers and over 1,000 verified international DMCs.' },
  { icon: 'ri-whatsapp-line', title: 'Intelligent Comms', text: 'Engage travelers where they are—WhatsApp, social media, and web—all from one dashboard.' },
  { icon: 'ri-bank-card-line', title: 'Financial Precision', text: 'Automated invoicing, supplier ledgers, and secure payment tracking built for travel pros.' },
  { icon: 'ri-bar-chart-2-line', title: 'Growth Analytics', text: 'Deep-dive reporting that turns raw booking data into strategic business decisions.' },
  { icon: 'ri-customer-service-2-line', title: 'Expert Support', text: 'Access a dedicated support ecosystem that understands the nuances of the travel industry.' },
];

// Highlight component for consistent branding
const BrandHighlight = ({ children, color = "#0d6efd" }) => (
  <span style={{ 
    color: color, 
    fontWeight: '700', 
    padding: '0 4px',
    borderRadius: '4px',
    backgroundColor: 'rgba(13, 110, 253, 0.05)',
    display: 'inline-block'
  }}>
    {children}
  </span>
);

export default function AboutPage() {
  return (
    <div style={{ backgroundColor: '#ffffff', fontFamily: "'Inter', sans-serif", color: '#1e293b' }}>
      
      {/* --- HERO SECTION --- */}
      <section className="py-5 overflow-hidden" style={{ 
        background: 'linear-gradient(135deg, #061121 0%, #0d6efd 100%)',
        padding: '140px 0',
        position: 'relative'
      }}>
        <div className="hero-pattern"></div>
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row align-items-center">
            <div className="col-lg-8 text-white text-center text-lg-start">
              <span className="badge rounded-pill mb-3 px-3 py-2" style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', letterSpacing: '1px' }}>
                <i className="ri-award-line me-2"></i> THE DEVELOPERS CODE STORY
              </span>
              <h1 className="display-2 fw-bold mb-4 text-white" style={{ letterSpacing: '-2px', lineHeight: '1' }}>
                Engineering the <br /> <span style={{ color: '#fff' }}>Future of Travel.</span>
              </h1>
              <p className="lead opacity-75 mb-0 fs-4 fw-light" style={{ maxWidth: '750px' }}>
                The journey of <span className="fw-bold text-white">Developers Code</span> began with a mission to simplify the world's most complex travel workflows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- STORY & PRODUCT SECTION --- */}
      <section className="py-5" style={{ marginTop: '-80px', position: 'relative', zIndex: 5 }}>
        <div className="container">
          {/* Card 1: Who We Are */}
          <div className="card border-0 shadow-lg p-lg-5 p-4 rounded-5 bg-white mb-5 transition-hover">
            <div className="row align-items-center g-5">
              <div className="col-lg-7">
                <div className="d-flex align-items-center mb-3">
                    <div className="p-2 bg-primary-soft rounded-3 me-3"><i className="ri-team-line text-primary fs-4"></i></div>
                    <h6 className="text-primary fw-bold mb-0" style={{ letterSpacing: '1px' }}>ESTABLISHED EXCELLENCE</h6>
                </div>
                <h2 className="display-5 fw-bold mb-4 text-dark" style={{ letterSpacing: '-1px' }}>A Vision to Simplify Complexity.</h2>
                <p className="text-muted fs-5 lh-lg mb-4">
                  <BrandHighlight>Saar Developers Code Private Limited</BrandHighlight> was established to solve a singular problem: Travel technology was too fragmented. Agents were losing hours switching between tabs and manually creating itineraries.
                </p>
                <p className="text-muted fs-5 lh-lg mb-0">
                  By blending enterprise engineering with travel expertise, we ensure that <BrandHighlight>Developers Code</BrandHighlight> remains the silent engine powering the growth of modern travel agencies.
                </p>
              </div>
              <div className="col-lg-5">
                <div className="p-5 bg-light rounded-5 text-center border h-100 d-flex flex-column justify-content-center border-primary border-opacity-10">
                   <h4 className="fw-bold mb-3 text-primary">Our Impact</h4>
                   <p className="text-secondary mb-0">Empowering hundreds of agencies globally through the technical prowess of <span className="fw-bold">Developers Code</span>.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: The Product */}
          <div className="card border-0 shadow-lg p-lg-5 p-4 rounded-5" style={{ background: 'linear-gradient(to right, #f8faff, #ffffff)', border: '1px solid #eef2f8 !important' }}>
            <div className="row g-5 align-items-center">
              <div className="col-lg-7">
                <div className="d-flex align-items-center mb-3">
                    <div className="p-2 bg-primary rounded-3 me-3"><i className="ri-rocket-fill text-white fs-4"></i></div>
                    <h6 className="text-primary fw-bold mb-0">THE FLAGSHIP ECOSYSTEM</h6>
                </div>
                <h2 className="fw-bold mb-4 text-dark display-6" style={{ letterSpacing: '-1px' }}>Introducing <span style={{ color: '#0d6efd', textDecoration: 'underline' }}>Zotrai</span></h2>
                <p className="text-muted fs-5 lh-lg mb-4">
                  <BrandHighlight>Zotrai</BrandHighlight> is more than just software; it is a single source of truth. As the flagship SaaS product from the house of <span className="fw-bold">Developers Code</span>, it streamlines:
                </p>
                <div className="row g-3">
                    <div className="col-md-6"><div className="p-3 bg-white border rounded-4 shadow-sm fw-bold"><i className="ri-checkbox-circle-line text-success me-2"></i> B2C, B2B & Corporate Portals</div></div>
                    <div className="col-md-6"><div className="p-3 bg-white border rounded-4 shadow-sm fw-bold"><i className="ri-checkbox-circle-line text-success me-2"></i> AI Itinerary Builder</div></div>
                    <div className="col-md-6"><div className="p-3 bg-white border rounded-4 shadow-sm fw-bold"><i className="ri-checkbox-circle-line text-success me-2"></i> Multi-Supplier API</div></div>
                    <div className="col-md-6"><div className="p-3 bg-white border rounded-4 shadow-sm fw-bold"><i className="ri-checkbox-circle-line text-success me-2"></i> Travel Accounting</div></div>
                </div>
              </div>
              <div className="col-lg-5">
                  <div className="p-5 bg-dark rounded-5 text-white shadow-xl position-relative overflow-hidden" style={{ background: '#0f172a' }}>
                      <div className="position-relative" style={{ zIndex: 1 }}>
                        <h4 className="fw-bold mb-3 text-info">The Zotrai Promise</h4>
                        <p className="opacity-75 mb-0 fs-5 lh-base italic">
                          "To democratize high-end technology, giving every agency the tools once reserved only for industry giants."
                        </p>
                      </div>
                      <i className="ri-double-quotes-r position-absolute" style={{ right: '20px', bottom: '10px', fontSize: '100px', opacity: 0.1 }}></i>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- WHY CHOOSE US (Highlighting Name in Heading) --- */}
      <section className="py-5 mb-5">
        <div className="container">
          <div className="text-center">
            <h2 className="display-6 fw-bold">Why Leading Agencies Choose <span className="text-primary">Zotrai</span></h2>
            <div className="mx-auto mt-2" style={{ height: '4px', width: '60px', backgroundColor: '#0d6efd', borderRadius: '2px' }}></div>
          </div>
          <div className="row justify-content-center g-4">
            {whyChooseUs.map((item, index) => (
              <div className="col-lg-4 col-md-6" key={index}>
                <div className="p-4 border rounded-5 h-100 feature-hover transition-all bg-white shadow-sm">
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3 p-3 rounded-4 bg-primary-soft text-primary"><i className={`${item.icon} fs-3`}></i></div>
                    <h4 className="fw-bold mb-0 text-dark">{item.title}</h4>
                  </div>
                  <p className="text-muted mb-0 lh-base">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
        
        .hero-pattern {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background-image: radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 30px 30px;
          opacity: 0.3;
        }

        .bg-primary-soft { background-color: #f0f7ff; }
        .rounded-5 { border-radius: 2rem !important; }
        
        .transition-hover { transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1); }
        .transition-hover:hover { transform: translateY(-8px); box-shadow: 0 40px 80px rgba(0,0,0,0.12) !important; }
        
        .feature-hover { border: 1px solid #f1f5f9; }
        .feature-hover:hover {
          border-color: #0d6efd !important;
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(13, 110, 253, 0.08) !important;
        }

        .italic { font-style: italic; }
      `}} />
    </div>
  );
}